import { queryOptions, useQuery } from '@tanstack/react-query';
import type Big from 'big.js';
import dayjs from 'dayjs';

import { SECONDS_IN_DAY } from '@cast/constants';
import type { DateRange } from '@cast/shared-types';
import { WorkloadTrafficDestinationHistoryItem } from '@cast/types';
import { toBig } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOAD_TRAFFIC_DESTINATIONS_HISTORY_QUERY_KEY =
  'workload-traffic-destinations-history';

const getWorkloadTrafficDestinationsHistoryQueryKey = ({
  clusterId,
  namespace,
  workloadType,
  workloadName,
  startTime,
  endTime,
  stepSeconds,
  limit,
}: {
  clusterId?: string;
  namespace?: string;
  workloadType?: string;
  workloadName?: string;
  startTime?: string;
  endTime?: string;
  stepSeconds?: number;
  limit?: number;
} = {}) =>
  [
    WORKLOAD_TRAFFIC_DESTINATIONS_HISTORY_QUERY_KEY,
    ...(clusterId !== undefined
      ? [
          clusterId,
          namespace,
          workloadType,
          workloadName,
          startTime,
          endTime,
          stepSeconds,
          limit,
        ]
      : []),
  ] as const;

type UseWorkloadTrafficDestinationsHistoryQuery = {
  clusterId: string;
  namespace: string;
  workloadType: string;
  workloadName: string;
  range: DateRange;
  stepSeconds?: number;
  limit?: number;
  enabled?: boolean;
};

type WorkloadTrafficDestinationsHistoryQuery = {
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  data?: WorkloadTrafficDestinationHistoryItem<Big>[];
};

const getWorkloadTrafficDestinationsHistoryQueryOptions = ({
  clusterId,
  namespace,
  workloadType,
  workloadName,
  range: [from, to],
  stepSeconds = SECONDS_IN_DAY,
  limit = 5,
  enabled = true,
}: UseWorkloadTrafficDestinationsHistoryQuery) =>
  queryOptions({
    queryKey: getWorkloadTrafficDestinationsHistoryQueryKey({
      clusterId,
      namespace,
      workloadType,
      workloadName,
      startTime: from.toISOString(),
      endTime: to.toISOString(),
      stepSeconds,
      limit,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getWorkloadTrafficDestinationsHistory({
          clusterId,
          namespace,
          workloadType,
          workloadName,
          startTime: from.toISOString(),
          endTime: to.toISOString(),
          stepSeconds,
          limit,
        });

      return (
        data.destinations?.map((item) => ({
          destination: {
            ...item.destination,
            ingress: {
              interZoneBytes: toBig(
                item.destination?.ingress?.interZoneBytes || 0
              ),
              interZoneCost: toBig(
                item.destination?.ingress?.interZoneCost || 0
              ),
            },
            egress: {
              interZoneBytes: toBig(
                item.destination?.egress?.interZoneBytes || 0
              ),
              interZoneCost: toBig(
                item.destination?.egress?.interZoneCost || 0
              ),
            },
          },
          history: item.history?.map((item) => ({
            ...item,
            timestamp: dayjs
              .utc(item.timestamp)
              .subtract(1, 'day')
              .toISOString(),
            ingress: {
              interZoneBytes: toBig(item.ingress?.interZoneBytes || 0),
              interZoneCost: toBig(item.ingress?.interZoneCost || 0),
            },
            egress: {
              interZoneBytes: toBig(item.egress?.interZoneBytes || 0),
              interZoneCost: toBig(item.egress?.interZoneCost || 0),
            },
          })),
        })) || []
      );
    },
    enabled,
  });

export const useWorkloadTrafficDestinationsHistoryQuery = (
  props: UseWorkloadTrafficDestinationsHistoryQuery
): WorkloadTrafficDestinationsHistoryQuery => {
  const { data, error, refetch, isLoading, isFetching } = useQuery(
    getWorkloadTrafficDestinationsHistoryQueryOptions(props)
  );

  return { data, error, refetch, isLoading, isFetching };
};
